import React, { useEffect, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Chip, Stack, Divider, Button, Alert, CircularProgress } from '@mui/material';
import { ArrowBack as BackIcon, Bolt as ApiIcon, PhoneIphone as AppIcon } from '@mui/icons-material';
import { PanelItem, Review, AprovadorError, aprovadorApi, brl, dataBR } from '@/services/aprovador';
import CodigoPagamento from './CodigoPagamento';
import { DUE, FORMA } from './ItemCard';

interface Props {
  token: string;
  item: PanelItem | null;
  onClose: () => void;
  onSessionLost: () => void;
  /** Recebe a conferência já feita, para o diálogo de confirmação não consultar o banco de novo. */
  onAprovar: (i: PanelItem, review: Review) => void;
  onPaguei: (i: PanelItem) => void;
  onRejeitar: (i: PanelItem) => void;
  perms: { canPayApi: boolean; canMarkPaid: boolean; canReject: boolean };
}

const Campo: React.FC<{ rotulo: string; children: React.ReactNode }> = ({ rotulo, children }) => (
  <Box sx={{ py: 1 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>{rotulo}</Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{children}</Typography>
  </Box>
);

/** Página de detalhes de um título (tela cheia; o "voltar" do celular fecha). */
const ItemDetail: React.FC<Props> = ({ token, item, onClose, onSessionLost, onAprovar, onPaguei, onRejeitar, perms }) => {
  const [review, setReview] = useState<Review | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [erroConf, setErroConf] = useState<string | null>(null);

  // PIX pela API: a conferência no banco (recebedor) é feita JÁ ao abrir — o Aprovar só
  // libera com o recebedor na tela. Não move dinheiro.
  const podeConferir = !!item && item.payVia === 'api' && item.status === 'pending' && perms.canPayApi;
  const conferir = async () => {
    if (!item) return;
    setConferindo(true); setErroConf(null);
    try {
      setReview(await aprovadorApi.conferir(token, item.id));
    } catch (e) {
      const err = e as AprovadorError;
      if (err.status === 401) onSessionLost(); else setErroConf(err.message);
    } finally { setConferindo(false); }
  };
  useEffect(() => {
    setReview(null); setErroConf(null);
    if (podeConferir) conferir();
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Empilha um estado no histórico para o botão voltar do Android/PWA fechar a página
  useEffect(() => {
    if (!item) return undefined;
    window.history.pushState({ aprovarDetalhe: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [item?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fechar = () => {
    if (window.history.state && window.history.state.aprovarDetalhe) window.history.back();
    else onClose();
  };

  if (!item) return null;
  const due = DUE[item.dueStatus];
  const processing = item.status === 'processing';
  const acao = (fn: (i: PanelItem) => void) => () => { fn(item); };

  return (
    <Dialog open fullScreen onClose={fechar} PaperProps={{ sx: { bgcolor: '#f3f5f8' } }}>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: '#1f2a37', color: '#fff', display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 1 }}>
        <IconButton color="inherit" onClick={fechar}><BackIcon /></IconButton>
        <Typography variant="subtitle1" fontWeight={700} noWrap>Detalhes do pagamento</Typography>
      </Box>

      <Box sx={{ maxWidth: 640, width: '100%', mx: 'auto', p: 2, pb: 'calc(96px + env(safe-area-inset-bottom))' }}>
        <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, p: 2 }}>
          <Typography variant="h4" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{brl(item.amount)}</Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{item.beneficiaryName || item.pixKey || 'Favorecido não informado'}</Typography>
          <Typography variant="body2" color={item.dueStatus === 'vencido' ? 'error.main' : 'text.secondary'} fontWeight={600}>
            Vencimento {item.dueDate ? dataBR(item.dueDate) : '—'}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 0.75 }}>
            <Chip size="small" label={due.label} color={due.color} variant={due.color === 'default' ? 'outlined' : 'filled'} />
            {item.payVia === 'api'
              ? <Chip size="small" icon={<ApiIcon />} label="Paga pela API" sx={{ bgcolor: '#e6f4ea', color: '#1e6b34', '& .MuiChip-icon': { color: '#1e6b34' } }} />
              : <Chip size="small" icon={<AppIcon />} label="Pagar no app" sx={{ bgcolor: '#fff4e5', color: '#8a4b00', '& .MuiChip-icon': { color: '#8a4b00' } }} />}
            {item.bank === 'efi' && <Chip size="small" variant="outlined" label="Sai da conta Efí" />}
            <Chip size="small" variant="outlined" label={FORMA[item.subtype] || item.kindLabel} />
            {processing && <Chip size="small" color="info" label="Processando" />}
            {item.duplicateCount && <Chip size="small" color="secondary" label={`Repetido ${item.duplicateCount}×`} />}
          </Stack>
          {item.payVia === 'app' && item.appReason && <Alert severity="info" sx={{ mt: 1.5 }}>{item.appReason}</Alert>}
          {item.guiaVencida && <Alert severity="error" sx={{ mt: 1.5 }}>Guia vencida não quita nem no app: precisa ser reemitida.</Alert>}
          {podeConferir && (
            <Box sx={{ mt: 1.5, border: '1px solid', borderColor: review ? '#b7dfc3' : '#e3e8ef', bgcolor: review ? '#f1faf3' : '#fafbfc', borderRadius: 2, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>CONFERÊNCIA NO BANCO</Typography>
              {conferindo && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <CircularProgress size={16} /><Typography variant="body2">Consultando o recebedor…</Typography>
                </Stack>
              )}
              {review && !conferindo && (
                <>
                  <Typography variant="body1" fontWeight={700}>{review.recebedor.nome || '—'}</Typography>
                  {review.recebedor.documento && <Typography variant="body2" color="text.secondary">CPF/CNPJ {review.recebedor.documento}</Typography>}
                  <Typography variant="body2" color="text.secondary">Valor {brl(review.valor)}</Typography>
                  {item.beneficiaryName && review.recebedor.nome &&
                    !review.recebedor.nome.toLowerCase().includes(item.beneficiaryName.toLowerCase().split(' ')[0]) && (
                    <Alert severity="warning" sx={{ mt: 1 }}>O nome no banco é diferente do favorecido do pedido. Confira.</Alert>
                  )}
                  {review.saldo.disponivel != null && review.valor > review.saldo.disponivel && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      Saldo insuficiente {review.bank === 'efi' ? 'na Efí' : 'no Sicoob'} ({brl(review.saldo.disponivel)}).
                      {review.bank === 'efi' && ' Boleto e guia saem da Efí: transfira do Sicoob pelo botão “Transferir para a Efí”.'}
                    </Alert>
                  )}
                  {review.apiLimit && review.valor > review.apiLimit.available && (
                    <Alert severity="error" sx={{ mt: 1 }}>Passa do teto da API hoje ({brl(review.apiLimit.available)} restantes). Pague no app.</Alert>
                  )}
                </>
              )}
              {erroConf && !conferindo && (
                <>
                  <Alert severity="error" sx={{ mt: 1 }}>{erroConf}</Alert>
                  <Button size="small" sx={{ mt: 1 }} onClick={conferir}>Tentar de novo</Button>
                </>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}><CodigoPagamento item={item} /></Box>

        <Box sx={{ bgcolor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, px: 2, py: 1, mt: 2 }}>
          {item.description && <Campo rotulo="Descrição">{item.description}</Campo>}
          {item.beneficiaryDocument && <><Divider /><Campo rotulo="CPF/CNPJ do favorecido">{item.beneficiaryDocument}</Campo></>}
          {item.pixKey && <><Divider /><Campo rotulo={`Chave PIX${item.pixKeyType ? ` (${item.pixKeyType})` : ''}`}>{item.pixKey}</Campo></>}
          <Divider /><Campo rotulo="Forma">{FORMA[item.subtype] || item.kindLabel}</Campo>
          {item.requestedByName && <><Divider /><Campo rotulo="Solicitado por">{item.requestedByName}</Campo></>}
          {item.appName && <><Divider /><Campo rotulo="Origem">{item.appName}</Campo></>}
          {item.externalId && <><Divider /><Campo rotulo="Referência">{item.externalId}</Campo></>}
          <Divider />
          <Campo rotulo="Pedido criado em">
            {new Date(item.createdAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', dateStyle: 'short', timeStyle: 'short' })}
          </Campo>
        </Box>
      </Box>

      {!processing && (
        <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, bgcolor: '#fff', borderTop: '1px solid #dfe5ec', boxShadow: 6,
          px: 2, pt: 1.5, pb: 'calc(12px + env(safe-area-inset-bottom))' }}>
          <Stack direction="row" spacing={1} sx={{ maxWidth: 640, mx: 'auto' }}>
            {perms.canReject && <Button color="error" onClick={acao(onRejeitar)}>Rejeitar</Button>}
            <Box sx={{ flex: 1 }} />
            {perms.canMarkPaid && (
              <Button variant={item.payVia === 'app' ? 'contained' : 'outlined'} onClick={acao(onPaguei)}>Paguei no app</Button>
            )}
            {item.payVia === 'api' && perms.canPayApi && (
              <Button variant="contained" color="success" disabled={!review || conferindo}
                onClick={() => review && onAprovar(item, review)}>
                {conferindo ? 'Conferindo…' : 'Aprovar'}
              </Button>
            )}
          </Stack>
        </Box>
      )}
    </Dialog>
  );
};

export default ItemDetail;
